/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Helmet from "react-helmet";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  downloadGuestlistTemplate,
  postGuestlistBulk,
} from "../../api/orderApi";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import { buildStaffClubEventQuery } from "../../helpers/eventClubScope";
import {
  triggerBlobDownload,
} from "../../helpers/fileDownload";
import useAuth from "../../hooks/useAuth";
import Constant from "../../util/constant";
import { currentEventListResponce } from "../../store/features/event/eventSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import "./guestlistBulkImport.css";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const GUESTLIST_BULK_ACCEPT =
  ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

function eventClubId(ev) {
  if (!ev || typeof ev !== "object") return null;
  const c = ev.clubid ?? ev.clubId;
  if (c != null && c !== "") return c;
  if (ev.club && typeof ev.club === "object") {
    return ev.club._id ?? ev.club.id ?? null;
  }
  return ev.club ?? null;
}

function formatEventOptionLabel(ev) {
  const title = ev?.title || ev?.name || "Event";
  const raw = ev?.startdatetime ?? ev?.startDatetime ?? ev?.eventstartDate;
  if (raw) {
    const m = moment(raw);
    if (m.isValid()) {
      return `${title} — ${m.format("DD MMM YYYY, h:mm A")}`;
    }
  }
  return title;
}

function isGuestlistBulkFile(file) {
  if (!file || !(file instanceof File)) return false;
  const name = String(file.name || "").toLowerCase();
  if (name.endsWith(".xlsx") || name.endsWith(".csv")) return true;
  const type = String(file.type || "").toLowerCase();
  return (
    type.includes("spreadsheetml") ||
    type === "text/csv" ||
    type === "application/csv"
  );
}

function mapApiErrorMessage(err, t) {
  const status = err?.status;
  const backendMsg =
    typeof err === "string"
      ? err
      : typeof err?.message === "string"
        ? err.message
        : "";

  if (status === 401) return t("GUESTLIST_BULK_ERR_401");
  if (status === 403) return t("GUESTLIST_BULK_ERR_403");
  if (status === 422) {
    return backendMsg || t("GUESTLIST_BULK_ERR_422");
  }
  if (status === 400) {
    return backendMsg || t("GUESTLIST_BULK_ERR_400");
  }
  if (!status && backendMsg) return backendMsg;
  return t("GUESTLIST_BULK_ERR_NETWORK");
}

function exportIssueRowsCsv(issueRows) {
  const escape = (value) => {
    const s = String(value ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const headers = [
    "Row",
    "Status",
    "Name",
    "Phone",
    "Email",
    "Ticket Type",
    "Qty",
    "Message",
  ];
  const lines = [headers.join(",")];
  issueRows.forEach((row) => {
    lines.push(
      [
        row.row,
        row.status,
        row.buyername,
        row.buyerphone,
        row.buyeremail,
        row.tickettype,
        row.noofticket ?? "",
        row.error || row.message || "",
      ]
        .map(escape)
        .join(",")
    );
  });
  const blob = new Blob([lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  triggerBlobDownload(blob, "guestlist-import-issue-rows.csv");
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const TEMPLATE_COLUMN_DEFS = [
  {
    nameKey: "GUESTLIST_BULK_CHIP_NAME",
    descKey: "GUESTLIST_BULK_CHIP_NAME_DESC",
    required: true,
  },
  {
    nameKey: "GUESTLIST_BULK_CHIP_PHONE",
    descKey: "GUESTLIST_BULK_CHIP_PHONE_DESC",
    required: true,
  },
  {
    nameKey: "GUESTLIST_BULK_CHIP_EMAIL",
    descKey: "GUESTLIST_BULK_CHIP_EMAIL_DESC",
    required: true,
  },
  {
    nameKey: "GUESTLIST_BULK_CHIP_TICKET",
    descKey: "GUESTLIST_BULK_CHIP_TICKET_DESC",
    required: true,
  },
  {
    nameKey: "GUESTLIST_BULK_CHIP_QTY",
    descKey: "GUESTLIST_BULK_CHIP_QTY_DESC",
    required: false,
  },
];

function rowIssueMessage(row, t) {
  if (row?.status === "skipped") {
    return row?.message || t("GUESTLIST_BULK_ROW_SKIPPED_MSG");
  }
  return row?.error || row?.message || "—";
}

function resultStatusMeta(row, t) {
  if (row?.status === "success") {
    return {
      label: t("GUESTLIST_BULK_STATUS_SUCCESS"),
      cardClass: "is-success",
      badgeClass: "is-success",
    };
  }
  if (row?.status === "skipped") {
    return {
      label: t("GUESTLIST_BULK_STATUS_SKIPPED"),
      cardClass: "is-skipped",
      badgeClass: "is-skipped",
    };
  }
  return {
    label: t("GUESTLIST_BULK_STATUS_FAILED"),
    cardClass: "is-failed",
    badgeClass: "is-failed",
  };
}

function GuestlistBulkResultCard({ row, idx, t, labels, theme = "dark" }) {
  const isSuccess = row?.status === "success";
  const message = isSuccess
    ? t("GUESTLIST_BULK_ROW_SUCCESS_MSG")
    : rowIssueMessage(row, t);
  const status = resultStatusMeta(row, t);

  return (
    <article
      className={`guestlist-bulk-result-card ${status.cardClass} guestlist-bulk-result-card--${theme}`}
    >
      <div className="guestlist-bulk-result-card-head">
        <span className="guestlist-bulk-result-row-num">
          {labels.row} {row?.row ?? "—"}
        </span>
        <span className={`guestlist-bulk-result-status ${status.badgeClass}`}>
          {status.label}
        </span>
      </div>
      <dl className="guestlist-bulk-result-fields">
        <div className="guestlist-bulk-result-field">
          <dt>{labels.name}</dt>
          <dd>{row?.buyername ?? "—"}</dd>
        </div>
        <div className="guestlist-bulk-result-field">
          <dt>{labels.phone}</dt>
          <dd>{row?.buyerphone ?? "—"}</dd>
        </div>
        <div className="guestlist-bulk-result-field guestlist-bulk-result-field-wide">
          <dt>{labels.email}</dt>
          <dd className="guestlist-bulk-result-email">{row?.buyeremail ?? "—"}</dd>
        </div>
        <div className="guestlist-bulk-result-field">
          <dt>{labels.ticket}</dt>
          <dd>{row?.tickettype ?? "—"}</dd>
        </div>
        <div className="guestlist-bulk-result-field">
          <dt>{labels.qty}</dt>
          <dd>{row?.noofticket ?? "—"}</dd>
        </div>
      </dl>
      <p className={`guestlist-bulk-result-message ${status.badgeClass}`}>
        {message}
      </p>
    </article>
  );
}

/**
 * Bulk guestlist import via Excel — CLUB_MANAGER only.
 */
const GuestlistBulkImportContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { managedClubId } = useAuth();
  const events = useSelector((s) => s?.event?.currentEventListResponce ?? []);
  const fileInputRef = useRef(null);

  const effectiveClubId = useMemo(() => {
    if (managedClubId) return String(managedClubId).trim();
    return null;
  }, [managedClubId]);

  const [eventId, setEventId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadCsvBusy, setDownloadCsvBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [issueModalOpen, setIssueModalOpen] = useState(false);

  useEffect(() => {
    if (!effectiveClubId) return;
    const q = buildStaffClubEventQuery(effectiveClubId);
    if (!q) return;
    dispatch(currentEventListResponce(q));
  }, [effectiveClubId, dispatch, location.pathname]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !effectiveClubId) return;
      const q = buildStaffClubEventQuery(effectiveClubId);
      if (!q) return;
      dispatch(currentEventListResponce(q));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [effectiveClubId, dispatch]);

  useEffect(() => {
    setSelectedFile(null);
    setImportResult(null);
    setIssueModalOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [eventId]);

  const filteredEvents = useMemo(() => {
    return (events || []).filter((ev) => {
      if (ev?.eventstatus === Constant.GENERALKEYS.CANCEL) return false;
      const cid = eventClubId(ev);
      if (cid == null || cid === "") return false;
      if (String(cid) !== String(effectiveClubId)) return false;
      return true;
    });
  }, [events, effectiveClubId]);

  const selectedEvent = useMemo(
    () => filteredEvents.find((ev) => String(ev._id) === String(eventId)),
    [filteredEvents, eventId]
  );

  const resultRows = importResult?.results ?? [];
  const issueRows = useMemo(
    () => resultRows.filter((r) => r?.status === "failed" || r?.status === "skipped"),
    [resultRows]
  );
  const successCount = importResult?.successCount ?? 0;
  const failedCount = importResult?.failedCount ?? 0;
  const skippedCount = importResult?.skippedCount ?? 0;
  const totalRows = importResult?.totalRows ?? 0;
  const isAllFailed = failedCount > 0 && successCount === 0 && skippedCount === 0;
  const isPartialSuccess =
    successCount > 0 && (failedCount > 0 || skippedCount > 0);
  const hasIssues = failedCount > 0 || skippedCount > 0;

  const showErrorToast = (message) => {
    dispatch(
      showToast({
        show: false,
        message,
        type: "error",
      })
    );
  };

  const onDownloadTemplate = async (format = "xlsx") => {
    if (!eventId || downloadBusy || downloadCsvBusy) return;
    const setBusy = format === "csv" ? setDownloadCsvBusy : setDownloadBusy;
    setBusy(true);
    try {
      const eventTitle =
        selectedEvent?.title || selectedEvent?.name || "event";
      const { blob, filename } = await downloadGuestlistTemplate(eventId, {
        eventTitle,
        format,
      });
      triggerBlobDownload(blob, filename);
    } catch (err) {
      showErrorToast(mapApiErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setImportResult(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!isGuestlistBulkFile(file)) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      showErrorToast(t("GUESTLIST_BULK_FILE_TYPE"));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      showErrorToast(t("GUESTLIST_BULK_FILE_SIZE"));
      return;
    }
    setSelectedFile(file);
  };

  const onImport = async () => {
    if (!eventId) {
      showErrorToast(t("OFFLINE_SALE_EVENT_PLACEHOLDER"));
      return;
    }
    if (!selectedFile) {
      showErrorToast(t("GUESTLIST_BULK_FILE_REQUIRED"));
      return;
    }
    if (!isGuestlistBulkFile(selectedFile)) {
      showErrorToast(t("GUESTLIST_BULK_FILE_TYPE"));
      return;
    }
    if (selectedFile.size > MAX_FILE_BYTES) {
      showErrorToast(t("GUESTLIST_BULK_FILE_SIZE"));
      return;
    }
    if (importBusy) return;

    setImportBusy(true);
    setImportResult(null);
    setIssueModalOpen(false);
    try {
      // FormData (not JSON) — File bytes cannot be JSON-serialized.
      const response = await postGuestlistBulk(eventId, selectedFile);
      const data = response?.data ?? response;
      setImportResult(data);

      const succeeded = data?.successCount ?? 0;
      const failed = data?.failedCount ?? 0;
      const skipped = data?.skippedCount ?? 0;

      if (failed === 0 && skipped === 0 && succeeded > 0) {
        dispatch(
          showToast({
            show: false,
            message: t("GUESTLIST_BULK_SUCCESS_TOAST", { count: succeeded }),
            type: "success",
          })
        );
      } else if (failed > 0 || skipped > 0) {
        setIssueModalOpen(true);
        if (succeeded > 0) {
          dispatch(
            showToast({
              show: false,
              message: t("GUESTLIST_BULK_PARTIAL_RESULT_TOAST", {
                success: succeeded,
                failed,
                skipped,
              }),
              type: failed > 0 ? "warning" : "success",
            })
          );
        }
      }
    } catch (err) {
      showErrorToast(mapApiErrorMessage(err, t));
    } finally {
      setImportBusy(false);
    }
  };

  const pageTitle = [{ title: t("GUESTLIST_BULK_TITLE"), path: "#" }];

  const progressSteps = [
    {
      key: "event",
      label: t("GUESTLIST_BULK_PROGRESS_EVENT"),
      icon: "bx-calendar-event",
      done: Boolean(eventId),
      active: !eventId,
    },
    {
      key: "template",
      label: t("GUESTLIST_BULK_PROGRESS_TEMPLATE"),
      icon: "bx-download",
      done: Boolean(eventId),
      active: Boolean(eventId) && !selectedFile && !importResult,
    },
    {
      key: "upload",
      label: t("GUESTLIST_BULK_PROGRESS_UPLOAD"),
      icon: "bx-upload",
      done: Boolean(selectedFile || importResult),
      active: Boolean(eventId) && Boolean(selectedFile) && !importResult,
    },
    {
      key: "results",
      label: t("GUESTLIST_BULK_PROGRESS_RESULTS"),
      icon: "bx-list-check",
      done: Boolean(importResult),
      active: Boolean(importResult),
    },
  ];

  const tableLabels = {
    row: t("GUESTLIST_BULK_COL_ROW"),
    name: t("GUESTLIST_GUEST_NAME"),
    phone: t("GUESTLIST_GUEST_PHONE"),
    email: t("GUESTLIST_GUEST_EMAIL"),
    ticket: t("GUESTLIST_CATEGORY"),
    qty: t("GUESTLIST_QTY"),
    status: t("GUESTLIST_BULK_COL_STATUS"),
    message: t("GUESTLIST_BULK_COL_MESSAGE"),
  };

  return (
    <div className="pageContent categoryManagement club-staff-page-shell club-offline-sale-page club-guestlist-page club-guestlist-bulk-page">
      <Helmet>
        <title>{t("GUESTLIST_BULK_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("GUESTLIST_BULK_TITLE")}
        breadcrumbItem={t("GUESTLIST_BULK_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={true}
        BreadcrumbmainTitle={true}
      />

      {!effectiveClubId ? (
        <Card className="border-0">
          <Card.Body>
            <p className="text-danger mb-0">{t("CLUB_WEBSITE_NO_CLUB")}</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="guestlist-bulk-shell">
          <div className="guestlist-bulk-intro">
            <div className="guestlist-bulk-intro-icon" aria-hidden="true">
              <i className="bx bx-spreadsheet" />
            </div>
            <div>
              <p className="guestlist-bulk-intro-title">
                {t("GUESTLIST_BULK_INTRO_TITLE")}
              </p>
              <p className="guestlist-bulk-intro-text">
                {t("GUESTLIST_BULK_INTRO_TEXT")}
              </p>
            </div>
          </div>

          <div
            className="guestlist-bulk-progress"
            aria-label={t("GUESTLIST_BULK_TITLE")}
          >
            {progressSteps.map((step) => (
              <div
                key={step.key}
                className={`guestlist-bulk-progress-step${
                  step.done ? " is-done" : ""
                }${step.active ? " is-active" : ""}`}
              >
                <div className="guestlist-bulk-progress-dot">
                  <i className={`bx ${step.icon}`} />
                </div>
                <span className="guestlist-bulk-progress-label">
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          <Card
            className={`border-0 guestlist-bulk-step-card mb-3${
              !eventId ? " is-active" : eventId ? " is-complete" : ""
            }`}
          >
            <Card.Body>
              <div className="guestlist-bulk-step-head">
                <span className="guestlist-bulk-step-num">1</span>
                <div className="guestlist-bulk-step-title-wrap">
                  <h2 className="guestlist-bulk-step-title">
                    {t("GUESTLIST_BULK_STEP_EVENT")}
                  </h2>
                  <p className="guestlist-bulk-step-subtitle">
                    {t("GUESTLIST_BULK_STEP_EVENT_SUB")}
                  </p>
                </div>
              </div>
              <div className="guestlist-bulk-step-body">
                <Form.Group className="mb-0">
                  <Form.Label>{t("OFFLINE_SALE_EVENT")}</Form.Label>
                  <Form.Select
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                  >
                    <option value="">
                      {t("OFFLINE_SALE_EVENT_PLACEHOLDER")}
                    </option>
                    {filteredEvents.map((ev) => (
                      <option key={ev._id} value={ev._id}>
                        {formatEventOptionLabel(ev)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </Card.Body>
          </Card>

          <Card
            className={`border-0 guestlist-bulk-step-card mb-3${
              eventId && !selectedFile ? " is-active" : eventId ? " is-complete" : ""
            }`}
          >
            <Card.Body>
              <div className="guestlist-bulk-step-head">
                <span className="guestlist-bulk-step-num">2</span>
                <div className="guestlist-bulk-step-title-wrap">
                  <h2 className="guestlist-bulk-step-title">
                    {t("GUESTLIST_BULK_STEP_TEMPLATE")}
                  </h2>
                  <p className="guestlist-bulk-step-subtitle">
                    {t("GUESTLIST_BULK_STEP_TEMPLATE_SUB")}
                  </p>
                </div>
              </div>
              <div className="guestlist-bulk-step-body">
                <p className="small text-muted mb-3">
                  {t("GUESTLIST_BULK_TEMPLATE_HELP")}
                </p>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <CustomButton
                    type="button"
                    className="fillButton club-staff-btn-wide-mobile"
                    disabled={!eventId || downloadBusy || downloadCsvBusy}
                    onClick={() => onDownloadTemplate("xlsx")}
                  >
                    {downloadBusy ? (
                      t("LOADING")
                    ) : (
                      <>
                        <i className="bx bx-download me-1" aria-hidden="true" />
                        {t("GUESTLIST_BULK_DOWNLOAD_TEMPLATE")}
                      </>
                    )}
                  </CustomButton>
                  <CustomButton
                    type="button"
                    className="outlineButton club-staff-btn-wide-mobile"
                    disabled={!eventId || downloadBusy || downloadCsvBusy}
                    onClick={() => onDownloadTemplate("csv")}
                  >
                    {downloadCsvBusy ? (
                      t("LOADING")
                    ) : (
                      <>
                        <i className="bx bx-download me-1" aria-hidden="true" />
                        {t("GUESTLIST_BULK_DOWNLOAD_CSV_TEMPLATE")}
                      </>
                    )}
                  </CustomButton>
                </div>
                <div className="guestlist-bulk-columns-hint">
                  <p className="small fw-semibold mb-2">
                    {t("GUESTLIST_BULK_COLUMNS_TITLE")}
                  </p>
                  <div className="guestlist-bulk-col-grid">
                    {TEMPLATE_COLUMN_DEFS.map((col) => (
                      <div key={col.nameKey} className="guestlist-bulk-col-chip">
                        <div className="guestlist-bulk-col-chip-top">
                          <span className="guestlist-bulk-col-name">
                            {t(col.nameKey)}
                          </span>
                          <span
                            className={`guestlist-bulk-col-badge ${
                              col.required ? "is-required" : "is-optional"
                            }`}
                          >
                            {col.required
                              ? t("GUESTLIST_BULK_BADGE_REQUIRED")
                              : t("GUESTLIST_BULK_BADGE_OPTIONAL")}
                          </span>
                        </div>
                        <p className="guestlist-bulk-col-desc">
                          {t(col.descKey)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card
            className={`border-0 guestlist-bulk-step-card mb-3${
              eventId && selectedFile && !importResult ? " is-active" : ""
            }${selectedFile || importResult ? " is-complete" : ""}`}
          >
            <Card.Body>
              <div className="guestlist-bulk-step-head">
                <span className="guestlist-bulk-step-num">3</span>
                <div className="guestlist-bulk-step-title-wrap">
                  <h2 className="guestlist-bulk-step-title">
                    {t("GUESTLIST_BULK_STEP_UPLOAD")}
                  </h2>
                  <p className="guestlist-bulk-step-subtitle">
                    {t("GUESTLIST_BULK_STEP_UPLOAD_SUB")}
                  </p>
                </div>
              </div>
              <div className="guestlist-bulk-step-body">
                <Form.Group className="mb-0">
                  <Form.Label className="visually-hidden">
                    {t("GUESTLIST_BULK_FILE_LABEL")}
                  </Form.Label>
                  <div
                    className={`guestlist-bulk-dropzone${
                      selectedFile ? " has-file" : ""
                    }${!eventId || importBusy ? " is-disabled" : ""}`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="guestlist-bulk-dropzone-input"
                      accept={GUESTLIST_BULK_ACCEPT}
                      onChange={onFileChange}
                      disabled={!eventId || importBusy}
                      aria-label={t("GUESTLIST_BULK_FILE_LABEL")}
                    />
                    {selectedFile ? (
                      <div className="guestlist-bulk-file-preview">
                        <div className="guestlist-bulk-file-preview-icon">
                          <i className="bx bxs-file-blank" aria-hidden="true" />
                        </div>
                        <div className="guestlist-bulk-file-preview-meta">
                          <p className="guestlist-bulk-file-preview-name">
                            {selectedFile.name}
                          </p>
                          <p className="guestlist-bulk-file-preview-size">
                            {formatFileSize(selectedFile.size)}
                          </p>
                        </div>
                        <span className="guestlist-bulk-file-change">
                          {t("GUESTLIST_BULK_DROP_CHANGE")}
                        </span>
                      </div>
                    ) : (
                      <>
                        <i
                          className="bx bx-cloud-upload guestlist-bulk-dropzone-icon"
                          aria-hidden="true"
                        />
                        <p className="guestlist-bulk-dropzone-title">
                          {t("GUESTLIST_BULK_DROP_TITLE")}
                        </p>
                        <p className="guestlist-bulk-dropzone-hint">
                          {t("GUESTLIST_BULK_DROP_HINT")}
                        </p>
                      </>
                    )}
                  </div>
                </Form.Group>
                <div className="guestlist-bulk-actions">
                  <CustomButton
                    type="button"
                    className="fillButton club-staff-btn-wide-mobile"
                    disabled={!eventId || !selectedFile || importBusy}
                    onClick={onImport}
                  >
                    {importBusy ? (
                      t("LOADING")
                    ) : (
                      <>
                        <i className="bx bx-import me-1" aria-hidden="true" />
                        {t("GUESTLIST_BULK_IMPORT_BTN")}
                      </>
                    )}
                  </CustomButton>
                </div>
              </div>
            </Card.Body>
          </Card>

          {importResult ? (
            <Card className="border-0 guestlist-bulk-step-card mb-3 is-active is-complete">
              <Card.Body>
                <div className="guestlist-bulk-step-head mb-3">
                  <span className="guestlist-bulk-step-num">4</span>
                  <div className="guestlist-bulk-step-title-wrap">
                    <h2 className="guestlist-bulk-step-title">
                      {t("GUESTLIST_BULK_STEP_RESULTS")}
                    </h2>
                  </div>
                </div>

                <div className="guestlist-bulk-step-body">
                  {hasIssues ? (
                    <Alert
                      variant="warning"
                      className="guestlist-bulk-warn-banner"
                    >
                      {t("GUESTLIST_BULK_WARN_PARTIAL", {
                        success: successCount,
                        failed: failedCount,
                        skipped: skippedCount,
                      })}
                    </Alert>
                  ) : null}

                  <div className="guestlist-bulk-summary row g-2 mb-3">
                    <div className="col-6 col-sm-3">
                      <div className="guestlist-bulk-stat">
                        <span className="guestlist-bulk-stat-value">
                          {totalRows}
                        </span>
                        <span className="guestlist-bulk-stat-label">
                          {t("GUESTLIST_BULK_TOTAL_ROWS")}
                        </span>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <div className="guestlist-bulk-stat guestlist-bulk-stat-success">
                        <span className="guestlist-bulk-stat-value">
                          {successCount}
                        </span>
                        <span className="guestlist-bulk-stat-label">
                          {t("GUESTLIST_BULK_SUCCESS_COUNT")}
                        </span>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <div className="guestlist-bulk-stat guestlist-bulk-stat-skipped">
                        <span className="guestlist-bulk-stat-value">
                          {skippedCount}
                        </span>
                        <span className="guestlist-bulk-stat-label">
                          {t("GUESTLIST_BULK_SKIPPED_COUNT")}
                        </span>
                      </div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <div className="guestlist-bulk-stat guestlist-bulk-stat-failed">
                        <span className="guestlist-bulk-stat-value">
                          {failedCount}
                        </span>
                        <span className="guestlist-bulk-stat-label">
                          {t("GUESTLIST_BULK_FAILED_COUNT")}
                        </span>
                      </div>
                    </div>
                  </div>

                  {issueRows.length > 0 ? (
                    <div className="mb-3 d-flex flex-wrap gap-2 guestlist-bulk-issue-actions">
                      <CustomButton
                        type="button"
                        className="outlineButton btn-sm"
                        onClick={() => setIssueModalOpen(true)}
                      >
                        {t("GUESTLIST_BULK_VIEW_ISSUE_ROWS")}
                      </CustomButton>
                      <CustomButton
                        type="button"
                        className="outlineButton btn-sm"
                        onClick={() => exportIssueRowsCsv(issueRows)}
                      >
                        {t("GUESTLIST_BULK_DOWNLOAD_ISSUE_CSV")}
                      </CustomButton>
                    </div>
                  ) : null}

                  {resultRows.length > 0 ? (
                    <div className="guestlist-bulk-result-list">
                      {resultRows.map((row, idx) => (
                        <GuestlistBulkResultCard
                          key={`${row?.row ?? idx}-${idx}`}
                          row={row}
                          idx={idx}
                          t={t}
                          labels={tableLabels}
                          theme="dark"
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </Card.Body>
            </Card>
          ) : null}

          <Modal
            show={issueModalOpen}
            onHide={() => setIssueModalOpen(false)}
            centered
            scrollable
            size="lg"
            backdropClassName="club-offline-modal-backdrop"
            dialogClassName="guestlist-bulk-failed-modal-dialog"
            className={`club-offline-sale-fail-modal guestlist-bulk-failed-modal${
              isPartialSuccess ? " guestlist-bulk-failed-modal--warning" : ""
            }`}
            contentClassName="border-0 rounded-3 shadow"
          >
            <Modal.Header closeButton className="border-0 pb-0">
              <Modal.Title
                className={`fw-bold fs-6 mb-0 ${
                  isAllFailed ? "text-danger" : "text-warning"
                }`}
              >
                {isAllFailed
                  ? t("GUESTLIST_BULK_FAILED_MODAL_TITLE_ALL")
                  : t("GUESTLIST_BULK_FAILED_MODAL_TITLE_PARTIAL")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
              <p className="small text-muted mb-2">
                {t("GUESTLIST_BULK_FAILED_MODAL_HINT")}
              </p>
              <p className="small text-muted mb-3">
                {t("GUESTLIST_BULK_FAILED_MODAL_SUMMARY", {
                  total: totalRows,
                  success: successCount,
                  failed: failedCount,
                  skipped: skippedCount,
                })}
              </p>
              <div className="guestlist-bulk-result-list guestlist-bulk-result-list--modal">
                {issueRows.map((row, idx) => (
                  <GuestlistBulkResultCard
                    key={`issue-${row?.row ?? idx}-${idx}`}
                    row={row}
                    idx={idx}
                    t={t}
                    labels={tableLabels}
                    theme="light"
                  />
                ))}
              </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => exportIssueRowsCsv(issueRows)}
              >
                {t("GUESTLIST_BULK_DOWNLOAD_ISSUE_CSV")}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm px-4"
                onClick={() => setIssueModalOpen(false)}
              >
                {t("OFFLINE_SALE_FAIL_OK")}
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default GuestlistBulkImportContainer;
